/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { removeDateTimeOffset } from "Utility/FormatHelper";

import {
  UPDATE_LISTING_CURRENT_PAGE,
  UPDATE_LISTING_PAGE_SIZE,
  UPDATE_USERPROFILE_ID,
  USERPROFILE_ADD_COMMENT,
  USERPROFILE_ADD_COMMENT_FAIL,
  USERPROFILE_ADD_COMMENT_SUCCESS,
  USERPROFILE_CLAIM_BENEFIT,
  USERPROFILE_CLAIM_BENEFIT_FAIL,
  USERPROFILE_CLAIM_BENEFIT_RESET,
  USERPROFILE_CLAIM_BENEFIT_SUCCESS,
  USERPROFILE_DL_LOAD,
  USERPROFILE_DL_LOAD_FAIL,
  USERPROFILE_DL_LOAD_SUCCESS,
  USERPROFILE_DL_REMOVE,
  USERPROFILE_DL_REMOVE_ALL,
  USERPROFILE_DL_REMOVE_ALL_FAIL,
  USERPROFILE_DL_REMOVE_ALL_SUCCESS,
  USERPROFILE_DL_REMOVE_FAIL,
  USERPROFILE_DL_REMOVE_SUCCESS,
  USERPROFILE_END_VSBENEFITS,
  USERPROFILE_END_VSBENEFITS_FAIL,
  USERPROFILE_END_VSBENEFITS_SUCCESS,
  USERPROFILE_EXTEND_VSBENEFITS,
  USERPROFILE_EXTEND_VSBENEFITS_FAIL,
  USERPROFILE_EXTEND_VSBENEFITS_SUCCESS,
  USERPROFILE_FETCH_PROGRAM_PROFILE,
  USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL,
  USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS,
  USERPROFILE_GET_PROFILE_BENEFITS,
  USERPROFILE_GET_PROFILE_BENEFITS_FAIL,
  USERPROFILE_GET_PROFILE_BENEFITS_SUCCESS,
  USERPROFILE_LOAD_COMMENTS_FAIL,
  USERPROFILE_LOAD_COMMENTS_SUCCESS,
  USERPROFILE_LOAD_FAIL,
  USERPROFILE_LOAD_SUCCESS,
  USERPROFILE_LOAD_VSBENEFITS_FAIL,
  USERPROFILE_LOAD_VSBENEFITS_SUCCESS,
  USERPROFILE_PROVISION_VSBENEFITS,
  USERPROFILE_PROVISION_VSBENEFITS_FAIL,
  USERPROFILE_PROVISION_VSBENEFITS_SUCCESS,
  USERPROFILE_UPDATE,
  USERPROFILE_UPDATE_BLOCK_SHIPPING,
  USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL,
  USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS,
  USERPROFILE_UPDATE_COMMENT_FAIL,
  USERPROFILE_UPDATE_COMMENT_SUCCESS,
  USERPROFILE_UPDATE_FAIL,
  USERPROFILE_UPDATE_IS_PRIVATE,
  USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  USERPROFILE_UPDATE_PROGRAM_PROFILE,
  USERPROFILE_UPDATE_PROGRAM_PROFILE_FAILURE,
  USERPROFILE_UPDATE_PROGRAM_PROFILE_SUCCESS,
  USERPROFILE_UPDATE_SUCCESS,
  USERPROFILE_VSBENEFITS_RESET,
  USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  USERPROFILES_FILTER_VALUE_ADD,
  USERPROFILES_FILTER_VALUE_REMOVE,
  USERPROFILES_FILTER_VALUE_RESET,
  USERPROFILES_FILTER_VALUE_UPDATE,
  USERPROFILES_SEARCH,
  USERPROFILES_SEARCH_FAIL,
  USERPROFILES_SEARCH_SUCCESS,
} from "../../Constants/ActionTypes";
import { defaultListingPageSize, defaultMlsaFilterValues } from "../../Constants/ProfileConstants";
import { IUserProfileState } from "./IUserProfileState";
import {
  IAddNewCommentFail,
  IClaimBenefit,
  IClaimBenefitFail,
  IClaimBenefitSuccess,
  IEndUserProfileVSBenefitsFail,
  IExtendUserProfileVSBenefitsFail,
  IFetchProgramProfile,
  IFetchProgramProfileFail,
  IFetchProgramProfileSuccess,
  IGetProfileBenefitsFail,
  IGetProfileBenefitsSuccess,
  ILoadUserProfileCommentsFail,
  ILoadUserProfileCommentsSuccess,
  ILoadUserProfileFail,
  ILoadUserProfileSuccess,
  ILoadUserProfileVSBenefitsFail,
  ILoadUserProfileVSBenefitsSuccess,
  IProvisionUserProfileVSBenefitsFail,
  ISearchUserProfiles,
  ISearchUserProfilesFail,
  ISearchUserProfilesSuccess,
  IUpdateCurrentPage,
  IUpdatePageSize,
  IUpdateProgramProfileFailure,
  IUpdateProgramProfileSuccess,
  IUpdateUserProfileCommentFail,
  IUpdateUserProfileFail,
  IUpdateUserProfileId,
  IUpdateUserProfileSuccess,
  IUserProfilesFilterValueAdd,
  IUserProfilesFilterValueRemove,
  IUserProfilesFilterValueUpdate,
  IUserProfileDlLoadFail,
  IUserProfileDlLoadSuccess,
  IUserProfileDlRemoveAllFail,
  IUserProfileDlRemoveFail,
  IUserProfileIsPrivateUpdateFail,
  IUserProfileUpdateBlockShippingFail,
  UserProfileActionTypes,
} from "./UserProfileAction";

const INITIAL_STATE: IUserProfileState = {
  userProfiles: [],
  userProfileFilterValues: defaultMlsaFilterValues,
  selectedUserProfile: {
    address1: "",
    address2: "",
    addressCity: "",
    addressCountryOrRegionName: "",
    addresses: [],
    addressPostCode: "",
    addressStateOrProvince: "",
    application: { applicationId: 0, dateModified: new Date(), applicationStatus: "" },
    awardNameLocalized: false,
    biography: "",
    companyName: "",
    companyRole: "",
    companySize: "",
    companyUrl: "",
    microsoftReferralId: "",
    createdBy: "",
    dateCreated: new Date(),
    dateDeleted: new Date(),
    dateModified: new Date(),
    dateOfBirth: undefined,
    deleted: false,
    deletedBy: "",
    firstName: "",
    fullPhoneNumber: "",
    genderName: "",
    genderPronounPreferenceName: "",
    headline: "",
    id: 0,
    isOnboardingWizardComplete: true,
    isPrivacyAgreed: false,
    isShippingBlocked: false,
    isTextOptIn: false,
    languages: [],
    lastName: "",
    levelStatus: { id: 0, levelName: "" },
    localizedFirstName: "",
    localizedLastName: "",
    modifiedBy: "",
    newSchoolAdded: false,
    onboardingWizardStep: 1,
    onboardingWizardStepId: 0,
    phoneContacts: [],
    pictureLink: "",
    primaryAddressId: 0,
    primaryEmail: "",
    primaryPhone: "",
    primaryPhoneCountryOrRegionId: 0,
    primaryPhoneId: 0,
    profilePictureBase64: "",
    profilePictureUrl: "",
    programProfileBenefit: [],
    programProfileStatusId: 0,
    programProfileStatusName: "",
    programProfileStartDate: new Date(),
    programProfileEndDate: new Date(),
    puid: "",
    renewalStatusName: "",
    screenNameLocalized: false,
    secondaryEmail: "",
    shippingAddress1: "",
    shippingAddress2: "",
    shippingAddressId: 0,
    shippingCity: "",
    shippingCompany: "",
    shippingCountryOrRegionId: 0,
    shippingCountryOrRegionName: "",
    shippingName: "",
    shippingPhoneCountryOrRegionId: 0,
    shippingPhoneId: 0,
    shippingPhoneNumber: "",
    shippingPostCode: "",
    shippingSameAsAddress: false,
    shippingStateOrProvince: "",
    shirtFitType: "",
    shirtSize: "",
    studentPartnerEmail: "",
    tenants: [],
    titleName: "",
    userProfileIdentifier: "",
    userProfileSchool: [],
    userProfileSocialNetwork: [],
    userProfileProgramLanguage: [],
  },
  error: "",
  userProfileId: 0,
  isUserProfilesLoading: true,
  isSelectedUserProfileUpdating: false,
  updateUserProfileError: "",
  isCurrentUserProfileLoading: true,
  isUserProfileCommentsLoading: false,
  isLoadingVSBenefits: false,
  loadVSBenefitsError: "",
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Idle,
  updateVSBenefitsError: "",
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
  programProfileUpdateError: "",
  fetchProgramProfileError: "",
  filteredCount: 0,
  isClaimingBenefit: false,
  isClaimingBenefitSuccess: false,
  claimBenefitError: "",
  benefitsOperationStatus: { read: OperationStatus.Idle, create: OperationStatus.Idle },
  profileBenefits: {
    azure: [],
    githubPro: [],
    mcp: [],
    linkedIn: [],
    visualStudio: [],
    m365: [],
    benefitTenant: {},
    teams: [],
    awardKits: [],
  },
  profileBenefitsErrorMessage: "",
  profileBenefitsStatus: OperationStatus.Idle,
  programProfileOperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  commentsState: {
    commentsOperationStatus: { read: OperationStatus.Idle, create: OperationStatus.Idle },
    userProfileComments: [],
    commentsErrorMessage: undefined,
  },
  updateBlockShippingOperationStatus: OperationStatus.Idle,
  updateBlockShippingErrorMessage: "",
  distributionLists: [],
  loadingDlStatus: OperationStatus.Idle,
  loadingDlErrorMessage: "",
  removingAllDlStatus: OperationStatus.Idle,
  removingAllErrorMessage: "",
  removingDlStatus: OperationStatus.Idle,
  removingDlErrorMessage: "",
  updateIsPrivateProfileStatus: OperationStatus.Idle,
  updateIsPrivateProfileError: "",
};

const applyUpdateProgramProfileSuccess = (
  state: IUserProfileState,
  action: IUpdateProgramProfileSuccess
): IUserProfileState => {
  return {
    ...state,
    programProfileUpdateError: "",
    programProfileOperationStatus: { update: OperationStatus.Succeeded },
    selectedUserProfile: {
      ...state.selectedUserProfile,
      programProfileStatusName: action.programProfile.programProfileStatusName,
      levelStatus: {
        ...state.selectedUserProfile.levelStatus,
        levelName: action.programProfile.levelStatusName,
      },
    },
  };
};

const applyUpdateProgramProfileFailure = (
  state: IUserProfileState,
  action: IUpdateProgramProfileFailure
): IUserProfileState => {
  return {
    ...state,
    programProfileUpdateError: action.message,
    programProfileOperationStatus: { update: OperationStatus.Failed },
  };
};

const applyUpdateCurrentPage = (
  state: IUserProfileState,
  action: IUpdateCurrentPage
): IUserProfileState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IUserProfileState,
  action: IUpdatePageSize
): IUserProfileState => ({
  ...state,
  pageSize: action.pageSize,
});

const applySearchUserProfiles = (
  state: IUserProfileState,
  action: ISearchUserProfiles
): IUserProfileState => ({
  ...state,
  isUserProfilesLoading: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  pageSize: action.pageSize,
});

const applySearchUserProfilesSuccess = (
  state: IUserProfileState,
  action: ISearchUserProfilesSuccess
): IUserProfileState => ({
  ...state,
  userProfiles: action.userProfiles,
  currentPage: action.searchPage,
  pageSize: action.pageSize,
  isUserProfilesLoading: false,
  filteredCount: action.filteredCount,
});

const applySearchUserProfilesFail = (
  state: IUserProfileState,
  action: ISearchUserProfilesFail
): IUserProfileState => ({
  ...state,
  error: action.error,
  isUserProfilesLoading: false,
  currentPage: 1,
  pageSize: defaultListingPageSize,
});

const addUserProfilesFilterValue = (
  state: IUserProfileState,
  action: IUserProfilesFilterValueAdd
): IUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.concat([action.filterValue]),
});

const removeUserProfilesFilterValue = (
  state: IUserProfileState,
  action: IUserProfilesFilterValueRemove
): IUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeUserProfilesFilterProvinceValue = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.filter(
    (item) => !(item.fieldName === "regionState")
  ),
});

const updateUserProfilesFilterValue = (
  state: IUserProfileState,
  action: IUserProfilesFilterValueUpdate
): IUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applyLoadUserProfile = (
  state: IUserProfileState,
  action: ILoadUserProfileSuccess
): IUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...action.userProfile,
    dateOfBirth: removeDateTimeOffset(action.userProfile.dateOfBirth),
  },
  isCurrentUserProfileLoading: false,
  programProfileUpdateError: "",
  updateBlockShippingOperationStatus: OperationStatus.Idle,
});

const applyLoadFailUserProfile = (
  state: IUserProfileState,
  action: ILoadUserProfileFail
): IUserProfileState => ({
  ...state,
  isCurrentUserProfileLoading: false,
  error: action.error,
});

const applyUpdateUserProfileId = (
  state: IUserProfileState,
  action: IUpdateUserProfileId
): IUserProfileState => {
  if (state.userProfileId === action.userProfileId) return state;

  return {
    ...state,
    userProfileId: action.userProfileId,
    isCurrentUserProfileLoading: true,
    updateUserProfileError: "",
  };
};

const applyUpdateUserProfile = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isSelectedUserProfileUpdating: true,
  updateUserProfileError: "",
});

const applyUpdateFailUserProfile = (
  state: IUserProfileState,
  action: IUpdateUserProfileFail
): IUserProfileState => ({
  ...state,
  isSelectedUserProfileUpdating: false,
  updateUserProfileError: action.error,
});

const applyUpdateSuccessUserProfile = (
  state: IUserProfileState,
  action: IUpdateUserProfileSuccess
): IUserProfileState => {
  return {
    ...state,
    userProfiles: state.userProfiles.map((item) =>
      item.id === action.userProfile.id ? action.userProfile : item
    ),
    isSelectedUserProfileUpdating: false,
    selectedUserProfile: {
      ...action.userProfile,
      dateOfBirth: removeDateTimeOffset(action.userProfile.dateOfBirth),
    },
  };
};

const applyLoadUserProfileComments = (
  state: IUserProfileState,
  action: ILoadUserProfileCommentsSuccess
): IUserProfileState => ({
  ...state,
  isUserProfileCommentsLoading: true,
  commentsState: {
    commentsOperationStatus: { read: OperationStatus.Succeeded, create: OperationStatus.Idle },
    userProfileComments: action.userProfileComments,
    commentsErrorMessage: undefined,
  },
});

const applyLoadFailUserProfileComments = (
  state: IUserProfileState,
  action: ILoadUserProfileCommentsFail
): IUserProfileState => ({
  ...state,
  isUserProfileCommentsLoading: false,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { read: OperationStatus.Failed, create: OperationStatus.Idle },
    commentsErrorMessage: action.loadCommentsError,
  },
});

const applyUpdateUserProfileComment = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { read: OperationStatus.Failed, update: OperationStatus.Pending },
    commentsErrorMessage: undefined,
  },
});

const applyUpdateFailUserProfileComment = (
  state: IUserProfileState,
  action: IUpdateUserProfileCommentFail
): IUserProfileState => ({
  ...state,
  error: action.error,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { read: OperationStatus.Failed, update: OperationStatus.Failed },
  },
});

const applyLoadUserProfileVSBenefitsSuccess = (
  state: IUserProfileState,
  action: ILoadUserProfileVSBenefitsSuccess
): IUserProfileState => ({
  ...state,
  userProfileVSBenefit: action.userProfileVSBenefit,
  isLoadingVSBenefits: false,
  loadVSBenefitsError: "",
  programProfileUpdateError: "",
});

const applyLoadUserProfileVSBenefitsFail = (
  state: IUserProfileState,
  action: ILoadUserProfileVSBenefitsFail
): IUserProfileState => ({
  ...state,
  isLoadingVSBenefits: false,
  loadVSBenefitsError: action.error,
});

const applyUpdateUserProfileVSBenefits = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isLoadingVSBenefits: true,
  updatingVSBenefitOperationStatus: OperationStatus.Pending,
  programProfileOperationStatus: { update: OperationStatus.Pending },
});

const applyProvisionUserProfileVSBenefitsSuccess = (
  state: IUserProfileState
): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Succeeded,
  updateVSBenefitsError: "",
});

const applyProvisionUserProfileVSBenefitsFail = (
  state: IUserProfileState,
  action: IProvisionUserProfileVSBenefitsFail
): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Failed,
  updateVSBenefitsError: action.error,
});

const applyExtendUserProfileVSBenefitsSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Succeeded,
});

const applyExtendUserProfileVSBenefitsFail = (
  state: IUserProfileState,
  action: IExtendUserProfileVSBenefitsFail
): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updateVSBenefitsError: action.error,
  updatingVSBenefitOperationStatus: OperationStatus.Failed,
});

const applyEndUserProfileVSBenefitsSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Succeeded,
});

const applyEndUserProfileVSBenefitsFail = (
  state: IUserProfileState,
  action: IEndUserProfileVSBenefitsFail
): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updateVSBenefitsError: action.error,
  updatingVSBenefitOperationStatus: OperationStatus.Failed,
});

const applyVSBenefitReset = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isUpdatingVSBenefits: false,
  updatingVSBenefitOperationStatus: OperationStatus.Idle,
  updateVSBenefitsError: "",
});

const applyClaimingBenefit = (
  state: IUserProfileState,
  action: IClaimBenefit
): IUserProfileState => ({
  ...state,
  isClaimingBenefit: true,
  isClaimingBenefitSuccess: false,
  claimBenefitError: "",
  benefitsOperationStatus: { create: OperationStatus.Pending },
});

const applyClaimingBenefitSuccess = (
  state: IUserProfileState,
  action: IClaimBenefitSuccess
): IUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitSuccess: true,
  benefitsOperationStatus: { create: OperationStatus.Succeeded },
});

const applyClaimingBenefitFail = (
  state: IUserProfileState,
  action: IClaimBenefitFail
): IUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitSuccess: false,
  claimBenefitError: action.error,
  benefitsOperationStatus: { create: OperationStatus.Failed },
});

const applyClaimingBenefitReset = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitSuccess: false,
  claimBenefitError: "",
  benefitsOperationStatus: { create: OperationStatus.Idle },
});

const applyGetProfileBenefits = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  profileBenefitsStatus: OperationStatus.Pending,
  profileBenefitsErrorMessage: "",
});

const applyGetProfileBenefitsSuccess = (
  state: IUserProfileState,
  action: IGetProfileBenefitsSuccess
): IUserProfileState => ({
  ...state,
  profileBenefitsStatus: OperationStatus.Succeeded,
  profileBenefits: action.userProfileBenefits,
});

const applyGetProfileBenefitsFail = (
  state: IUserProfileState,
  action: IGetProfileBenefitsFail
): IUserProfileState => ({
  ...state,
  profileBenefitsStatus: OperationStatus.Failed,
  profileBenefitsErrorMessage: action.profileBenefitsErrorMessage,
});

const applyFetchProgramProfile = (
  state: IUserProfileState,
  action: IFetchProgramProfile
): IUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: undefined,
  },
  programProfileOperationStatus: { read: OperationStatus.Pending },
});

const applyFetchProgramProfileSuccess = (
  state: IUserProfileState,
  action: IFetchProgramProfileSuccess
): IUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: action.programProfile,
  },
  programProfileOperationStatus: { read: OperationStatus.Succeeded },
});

const applyFetchProgramProfileFail = (
  state: IUserProfileState,
  action: IFetchProgramProfileFail
): IUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: undefined,
  },
  programProfileOperationStatus: { read: OperationStatus.Failed },
  fetchProgramProfileError: action.errorMessage,
});

const applyAddNewComment = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsErrorMessage: undefined,
    commentsOperationStatus: { create: OperationStatus.Pending },
  },
});

const applyAddNewCommentSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsErrorMessage: undefined,
    commentsOperationStatus: { create: OperationStatus.Succeeded },
  },
});

const applyAddNewCommentFail = (
  state: IUserProfileState,
  action: IAddNewCommentFail
): IUserProfileState => ({
  ...state,
  commentsState: {
    ...state.commentsState,
    commentsOperationStatus: { create: OperationStatus.Failed },
    commentsErrorMessage: action.errorMessage,
  },
});

const applyUpdateBlockShipping = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  updateBlockShippingOperationStatus: OperationStatus.Pending,
  updateBlockShippingErrorMessage: "",
});

const applyUpdateBlockShippingFail = (
  state: IUserProfileState,
  action: IUserProfileUpdateBlockShippingFail
): IUserProfileState => ({
  ...state,
  updateBlockShippingOperationStatus: OperationStatus.Failed,
  updateBlockShippingErrorMessage: action.errorMessage,
});

const applyUpdateBlockShippingSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  updateBlockShippingOperationStatus: OperationStatus.Succeeded,
});

const applyDlLoad = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  loadingDlStatus: OperationStatus.Pending,
});

const applyDlLoadSuccess = (
  state: IUserProfileState,
  action: IUserProfileDlLoadSuccess
): IUserProfileState => ({
  ...state,
  loadingDlStatus: OperationStatus.Succeeded,
  distributionLists: action.distributionLists,
});

const applyDlLoadFail = (
  state: IUserProfileState,
  action: IUserProfileDlLoadFail
): IUserProfileState => ({
  ...state,
  loadingDlStatus: OperationStatus.Failed,
  loadingDlErrorMessage: action.error,
});

const applyDlRemoveAll = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  removingAllDlStatus: OperationStatus.Pending,
  removingDlErrorMessage: "",
});

const applyDlRemoveAllSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  removingAllDlStatus: OperationStatus.Succeeded,
});

const applyDlRemoveAllFail = (
  state: IUserProfileState,
  action: IUserProfileDlRemoveAllFail
): IUserProfileState => ({
  ...state,
  removingAllDlStatus: OperationStatus.Failed,
  removingAllErrorMessage: action.error,
});

const applyDlRemove = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  removingDlStatus: OperationStatus.Pending,
  removingDlErrorMessage: "",
});

const applyDlRemoveSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  removingDlStatus: OperationStatus.Succeeded,
});

const applyDlRemoveFail = (
  state: IUserProfileState,
  action: IUserProfileDlRemoveFail
): IUserProfileState => ({
  ...state,
  removingDlStatus: OperationStatus.Failed,
  removingDlErrorMessage: action.error,
});

const applyUserProfileIsPrivateUpdate = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Pending,
  updateIsPrivateProfileError: "",
});

const applyUserProfileIsPrivateUpdateSuccess = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Succeeded,
});

const applyUserProfileIsPrivateUpdateFail = (
  state: IUserProfileState,
  action: IUserProfileIsPrivateUpdateFail
): IUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Failed,
  updateIsPrivateProfileError: action.error,
});

const applyUserProfileFilterValueReset = (state: IUserProfileState): IUserProfileState => ({
  ...state,
  userProfiles: [],
  userProfileFilterValues: defaultMlsaFilterValues,
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
});

function userProfileReducer(
  state = INITIAL_STATE,
  action: UserProfileActionTypes
): IUserProfileState {
  switch (action.type) {
    case USERPROFILE_GET_PROFILE_BENEFITS: {
      return applyGetProfileBenefits(state);
    }
    case USERPROFILE_GET_PROFILE_BENEFITS_SUCCESS: {
      return applyGetProfileBenefitsSuccess(state, action);
    }
    case USERPROFILE_GET_PROFILE_BENEFITS_FAIL: {
      return applyGetProfileBenefitsFail(state, action);
    }
    case UPDATE_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case USERPROFILES_SEARCH: {
      return applySearchUserProfiles(state, action);
    }
    case USERPROFILES_SEARCH_SUCCESS: {
      return applySearchUserProfilesSuccess(state, action);
    }
    case USERPROFILES_SEARCH_FAIL: {
      return applySearchUserProfilesFail(state, action);
    }
    case USERPROFILES_FILTER_VALUE_ADD: {
      return addUserProfilesFilterValue(state, action);
    }
    case USERPROFILES_FILTER_VALUE_REMOVE: {
      return removeUserProfilesFilterValue(state, action);
    }
    case USERPROFILES_FILTER_PROVINCE_VALUE_REMOVE: {
      return removeUserProfilesFilterProvinceValue(state);
    }
    case USERPROFILES_FILTER_VALUE_UPDATE: {
      return updateUserProfilesFilterValue(state, action);
    }
    case USERPROFILE_LOAD_SUCCESS: {
      return applyLoadUserProfile(state, action);
    }
    case USERPROFILE_LOAD_FAIL: {
      return applyLoadFailUserProfile(state, action);
    }
    case UPDATE_USERPROFILE_ID: {
      return applyUpdateUserProfileId(state, action);
    }
    case USERPROFILE_UPDATE: {
      return applyUpdateUserProfile(state);
    }
    case USERPROFILE_UPDATE_SUCCESS: {
      return applyUpdateSuccessUserProfile(state, action);
    }
    case USERPROFILE_UPDATE_FAIL: {
      return applyUpdateFailUserProfile(state, action);
    }
    case USERPROFILE_LOAD_COMMENTS_SUCCESS: {
      return applyLoadUserProfileComments(state, action);
    }
    case USERPROFILE_LOAD_COMMENTS_FAIL: {
      return applyLoadFailUserProfileComments(state, action);
    }
    case USERPROFILE_UPDATE_COMMENT_SUCCESS: {
      return applyUpdateUserProfileComment(state);
    }
    case USERPROFILE_UPDATE_COMMENT_FAIL: {
      return applyUpdateFailUserProfileComment(state, action);
    }
    case USERPROFILE_LOAD_VSBENEFITS_SUCCESS: {
      return applyLoadUserProfileVSBenefitsSuccess(state, action);
    }
    case USERPROFILE_LOAD_VSBENEFITS_FAIL: {
      return applyLoadUserProfileVSBenefitsFail(state, action);
    }
    case USERPROFILE_UPDATE_PROGRAM_PROFILE:
    case USERPROFILE_PROVISION_VSBENEFITS:
    case USERPROFILE_EXTEND_VSBENEFITS:
    case USERPROFILE_END_VSBENEFITS: {
      return applyUpdateUserProfileVSBenefits(state);
    }
    case USERPROFILE_PROVISION_VSBENEFITS_SUCCESS: {
      return applyProvisionUserProfileVSBenefitsSuccess(state);
    }
    case USERPROFILE_PROVISION_VSBENEFITS_FAIL: {
      return applyProvisionUserProfileVSBenefitsFail(state, action);
    }
    case USERPROFILE_EXTEND_VSBENEFITS_SUCCESS: {
      return applyExtendUserProfileVSBenefitsSuccess(state);
    }
    case USERPROFILE_EXTEND_VSBENEFITS_FAIL: {
      return applyExtendUserProfileVSBenefitsFail(state, action);
    }
    case USERPROFILE_END_VSBENEFITS_SUCCESS: {
      return applyEndUserProfileVSBenefitsSuccess(state);
    }
    case USERPROFILE_END_VSBENEFITS_FAIL: {
      return applyEndUserProfileVSBenefitsFail(state, action);
    }
    case USERPROFILE_VSBENEFITS_RESET: {
      return applyVSBenefitReset(state);
    }
    case USERPROFILE_UPDATE_PROGRAM_PROFILE_SUCCESS: {
      return applyUpdateProgramProfileSuccess(state, action);
    }
    case USERPROFILE_UPDATE_PROGRAM_PROFILE_FAILURE: {
      return applyUpdateProgramProfileFailure(state, action);
    }
    case USERPROFILE_CLAIM_BENEFIT: {
      return applyClaimingBenefit(state, action);
    }
    case USERPROFILE_CLAIM_BENEFIT_SUCCESS: {
      return applyClaimingBenefitSuccess(state, action);
    }
    case USERPROFILE_CLAIM_BENEFIT_FAIL: {
      return applyClaimingBenefitFail(state, action);
    }
    case USERPROFILE_CLAIM_BENEFIT_RESET: {
      return applyClaimingBenefitReset(state);
    }
    case USERPROFILE_FETCH_PROGRAM_PROFILE_SUCCESS: {
      return applyFetchProgramProfileSuccess(state, action);
    }
    case USERPROFILE_FETCH_PROGRAM_PROFILE_FAIL: {
      return applyFetchProgramProfileFail(state, action);
    }
    case USERPROFILE_FETCH_PROGRAM_PROFILE: {
      return applyFetchProgramProfile(state, action);
    }
    case USERPROFILE_ADD_COMMENT: {
      return applyAddNewComment(state);
    }
    case USERPROFILE_ADD_COMMENT_SUCCESS: {
      return applyAddNewCommentSuccess(state);
    }
    case USERPROFILE_ADD_COMMENT_FAIL: {
      return applyAddNewCommentFail(state, action);
    }
    case USERPROFILE_UPDATE_BLOCK_SHIPPING: {
      return applyUpdateBlockShipping(state);
    }
    case USERPROFILE_UPDATE_BLOCK_SHIPPING_FAIL: {
      return applyUpdateBlockShippingFail(state, action);
    }
    case USERPROFILE_UPDATE_BLOCK_SHIPPING_SUCCESS: {
      return applyUpdateBlockShippingSuccess(state);
    }
    case USERPROFILE_DL_LOAD: {
      return applyDlLoad(state);
    }
    case USERPROFILE_DL_LOAD_SUCCESS: {
      return applyDlLoadSuccess(state, action);
    }
    case USERPROFILE_DL_LOAD_FAIL: {
      return applyDlLoadFail(state, action);
    }
    case USERPROFILE_DL_REMOVE: {
      return applyDlRemove(state);
    }
    case USERPROFILE_DL_REMOVE_SUCCESS: {
      return applyDlRemoveSuccess(state);
    }
    case USERPROFILE_DL_REMOVE_FAIL: {
      return applyDlRemoveFail(state, action);
    }
    case USERPROFILE_DL_REMOVE_ALL: {
      return applyDlRemoveAll(state);
    }
    case USERPROFILE_DL_REMOVE_ALL_SUCCESS: {
      return applyDlRemoveAllSuccess(state);
    }
    case USERPROFILE_DL_REMOVE_ALL_FAIL: {
      return applyDlRemoveAllFail(state, action);
    }
    case USERPROFILE_UPDATE_IS_PRIVATE: {
      return applyUserProfileIsPrivateUpdate(state);
    }
    case USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS: {
      return applyUserProfileIsPrivateUpdateSuccess(state);
    }
    case USERPROFILE_UPDATE_IS_PRIVATE_FAIL: {
      return applyUserProfileIsPrivateUpdateFail(state, action);
    }
    case USERPROFILES_FILTER_VALUE_RESET: {
      return applyUserProfileFilterValueReset(state);
    }
    default:
      return state;
  }
}

export default userProfileReducer;
